/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

// Lib
import { apiClient } from '@lib/api-client';
import {
	queryClient,
	TMutationConfig,
} from '@lib/react-query';

// Types
import { TApiError } from '@global-types/api/api';
import {
	TApiResponseResetEmail,
	TBodyResetEmail,
} from '../types';

// Constants
import { queryKeys } from '@config/constants/query-keys';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const resetEmail = (
	body: TBodyResetEmail
): Promise<TApiResponseResetEmail> => {
	return apiClient.post('/users/reset_email', {
		...body,
	});
};

type TUseResetEmailOptions = {
	mutationConfig?: TMutationConfig<typeof resetEmail>;
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useResetEmail = ({
	mutationConfig,
}: TUseResetEmailOptions = {}) => {
	const { t } = useTranslation();
	const { onSuccess } = mutationConfig || {};
	const navigate = useNavigate();
	const {
		mutate,
		isPending: isLoading,
		isSuccess,
	} = useMutation({
		mutationFn: resetEmail,
		onSuccess: (...args) => {
			const data = args[0];
			onSuccess?.(...args);
			notifications.show({
				title: t('global.success'),
				message: t('auth.emailSuccessfullyUpdated'),
				color: 'var(--mantine-color-igluGreen-9)',
			});
			queryClient.setQueryData(
				[queryKeys.auth.user],
				data
			);
			navigate(`/login?email=${data.email}`);
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'var(--mantine-color-igluRed-9)',
			});
			localStorage.removeItem('user');
			navigate(`/`);
		},
	});

	return { mutate, isLoading, isSuccess };
};
